import { useRef, useState, useEffect, useCallback, RefObject } from 'react'

interface UseHorizontalScrollOptions {
  /** Delay in ms before checking scroll state (for async rendering) */
  initialCheckDelay?: number
  /** Threshold in px to determine if we can scroll (default 10) */
  scrollThreshold?: number
  /** Amount to scroll in px when using scroll functions (default 200) */
  scrollAmount?: number
  /** Dependencies that should trigger a re-check of scroll state */
  deps?: unknown[]
}

interface UseHorizontalScrollReturn {
  scrollRef: RefObject<HTMLDivElement>
  canScrollLeft: boolean
  canScrollRight: boolean
  scrollLeft: () => void
  scrollRight: () => void
}

/**
 * Hook for managing horizontal scroll state and controls.
 * Handles scroll position detection and provides scroll functions.
 */
export function useHorizontalScroll(
  options: UseHorizontalScrollOptions = {}
): UseHorizontalScrollReturn {
  const {
    initialCheckDelay = 50,
    scrollThreshold = 10,
    scrollAmount = 200,
    deps = [],
  } = options

  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el
      setCanScrollLeft(scrollLeft > scrollThreshold)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - scrollThreshold)
    }
  }, [scrollThreshold])

  useEffect(() => {
    const handleScroll = () => checkScroll()
    const handleResize = () => checkScroll()

    const attachListeners = () => {
      const el = scrollRef.current
      if (el) {
        el.addEventListener('scroll', handleScroll)
        window.addEventListener('resize', handleResize)
        checkScroll()
        return true
      }
      return false
    }

    // Check multiple times to handle async rendering
    const timeoutId1 = setTimeout(checkScroll, initialCheckDelay)
    const timeoutId2 = setTimeout(checkScroll, 200)

    // Try to attach immediately, retry if needed
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null
    if (!attachListeners()) {
      retryTimeoutId = setTimeout(attachListeners, 100)
    }

    return () => {
      clearTimeout(timeoutId1)
      clearTimeout(timeoutId2)
      if (retryTimeoutId) clearTimeout(retryTimeoutId)
      const el = scrollRef.current
      if (el) {
        el.removeEventListener('scroll', handleScroll)
      }
      window.removeEventListener('resize', handleResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkScroll, initialCheckDelay, ...deps])

  const scrollLeft = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      })
    }
  }, [scrollAmount])

  const scrollRight = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      })
    }
  }, [scrollAmount])

  return {
    scrollRef: scrollRef as RefObject<HTMLDivElement>,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
  }
}
